import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogCloseButton,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Box,
	Button,
	CheveronSmallLeftOutline,
	DotsHorizontalOutline,
	Flex,
	HStack,
	IconButton,
	Input,
	InputGroup,
	InputLeftElement,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	PencilAltOutline,
	PencilOutline,
	SearchAltOutline,
	Text,
	Tooltip,
	TrashOutline,
	useDisclosure,
	useToast,
	VStack,
	XOutline,
} from '@bonusly/design-system';

import {
	useBizyDeleteConversationMutation,
	useBizyUpdateConversationMutation,
	useBizyUserConversation,
	useBizyUserConversations,
} from '../../queries/bizy-conversations';
import { BizySidePanelHeader } from './bizy-side-panel-header';
import { generateConversationTitle } from './lib/conversation-title';
import { getCachedTitle, removeCachedTitle, setCachedTitle } from './lib/conversation-title-cache';
import { clearBizySession, loadBizySession } from './lib/session-storage';
import { useBizySidePanelStore } from './store/use-bizy-side-panel-store';
import { useBizyWidgetStore } from './store/use-bizy-widget-store';

interface BizySidePanelHistoryViewProps {
	onBack: () => void;
	onNewConversation: () => void;
	onResetChat: () => void;
}

type TimeGroup = 'today' | 'yesterday' | 'previous7Days' | 'older';

function getTimeGroup(date: Date): TimeGroup {
	const now = new Date();
	const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	if (date >= startOfToday) return 'today';

	const startOfYesterday = new Date(startOfToday);
	startOfYesterday.setDate(startOfToday.getDate() - 1);
	if (date >= startOfYesterday) return 'yesterday';

	const sevenDaysAgo = new Date(startOfToday);
	sevenDaysAgo.setDate(startOfToday.getDate() - 7);
	if (date >= sevenDaysAgo) return 'previous7Days';

	return 'older';
}

const TIME_GROUP_ORDER: TimeGroup[] = ['today', 'yesterday', 'previous7Days', 'older'];

const TIME_GROUP_KEYS: Record<TimeGroup, string> = {
	today: 'sidePanel.groupToday',
	yesterday: 'sidePanel.groupYesterday',
	previous7Days: 'sidePanel.groupPrevious7Days',
	older: 'sidePanel.groupOlder',
};

export function BizySidePanelHistoryView({ onBack, onNewConversation, onResetChat }: BizySidePanelHistoryViewProps) {
	const { t } = useTranslation('admin_bizy');
	const { track } = useAnalytics();
	const [searchValue, setSearchValue] = useState('');
	const [selectedConversationId, setSelectedConversationId] = useState<string | undefined>();
	const [renamingId, setRenamingId] = useState<string | undefined>();
	const [renameValue, setRenameValue] = useState('');
	const setResume = useBizyWidgetStore((s) => s.setResume);

	const toast = useToast();
	const closePanel = useBizySidePanelStore((state) => state.close);
	const { data: conversationsData, isLoading } = useBizyUserConversations();
	const { data: selectedConversation } = useBizyUserConversation(selectedConversationId);
	const { mutate: updateConversation } = useBizyUpdateConversationMutation();
	const { mutate: deleteConversationBackend } = useBizyDeleteConversationMutation();
	const conversations = conversationsData?.[0] || [];

	useEffect(() => {
		if (!selectedConversation || !selectedConversationId) return;
		setResume(selectedConversationId, selectedConversation.messages);
		onBack();
	}, [onBack, setResume, selectedConversation, selectedConversationId]);

	const getConversationTitle = (conversation: (typeof conversations)[0]) =>
		conversation.conversationTitle || getCachedTitle(conversation.conversationId) || generateConversationTitle(conversation.firstUserMessage);

	const filteredConversations = useMemo(() => {
		if (!searchValue.trim()) return conversations;
		const query = searchValue.toLowerCase();
		return conversations.filter((conversation) => {
			const title = getConversationTitle(conversation);
			return title.toLowerCase().includes(query);
		});
	}, [conversations, searchValue]); // eslint-disable-line react-hooks/exhaustive-deps -- getConversationTitle is stable

	const groupedConversations = useMemo(() => {
		const sorted = [...filteredConversations].sort((a, b) => b.lastMessageAt.getTime() - a.lastMessageAt.getTime());
		const groups = new Map<TimeGroup, typeof filteredConversations>();
		for (const conversation of sorted) {
			const group = getTimeGroup(conversation.lastMessageAt);
			const existing = groups.get(group) ?? [];
			existing.push(conversation);
			groups.set(group, existing);
		}
		return TIME_GROUP_ORDER.filter((g) => groups.has(g)).map((group) => ({ group, conversations: groups.get(group) ?? [] }));
	}, [filteredConversations]);

	const renameCommittedRef = useRef(false);

	const handleRename = (conversationId: string) => {
		renameCommittedRef.current = false;
		const conversation = conversations.find((c) => c.conversationId === conversationId);
		setRenameValue(conversation ? getConversationTitle(conversation) : '');
		setRenamingId(conversationId);
	};

	const handleFinishRename = () => {
		if (renameCommittedRef.current) return;
		renameCommittedRef.current = true;
		if (renamingId && renameValue.trim()) {
			setCachedTitle(renamingId, renameValue.trim());
			updateConversation({ conversationId: renamingId, title: renameValue.trim() });
			track(AnalyticsEvent.Bizy.ConversationRenamed, { path: window.location.pathname });
		}
		setRenamingId(undefined);
		setRenameValue('');
	};

	const deleteDialog = useDisclosure();
	const deleteCancelRef = useRef<HTMLButtonElement>(null);
	const [deletingConversationId, setDeletingConversationId] = useState<string | undefined>();

	const handleDeleteClick = (conversationId: string) => {
		setDeletingConversationId(conversationId);
		deleteDialog.onOpen();
	};

	const handleConfirmDelete = () => {
		if (deletingConversationId) {
			deleteConversationBackend(deletingConversationId, {
				onSuccess: () => {
					removeCachedTitle(deletingConversationId);
					const session = loadBizySession();
					if (session?.conversationId === deletingConversationId) {
						clearBizySession();
						onResetChat();
					}
					setDeletingConversationId(undefined);
					deleteDialog.onClose();
					toast({ title: t('sidePanel.deleteChatSuccess'), status: 'info', duration: 3000 });
					track(AnalyticsEvent.Bizy.ConversationDeleted, { path: window.location.pathname });
				},
				onError: () => {
					setDeletingConversationId(undefined);
					deleteDialog.onClose();
					toast({ title: t('sidePanel.deleteChatError'), status: 'error', duration: 3000 });
				},
			});
		} else {
			setDeletingConversationId(undefined);
			deleteDialog.onClose();
		}
	};

	return (
		<Flex direction="column" h="100%">
			<BizySidePanelHeader
				left={
					<HStack spacing={0}>
						<IconButton
							aria-label="Back"
							icon={<CheveronSmallLeftOutline boxSize={5} />}
							variant="ghost"
							size="sm"
							color="secondary.fg"
							_hover={{ bg: 'secondary.bgAlpha' }}
							onClick={onBack}
						/>
						<Text fontSize="md" fontWeight="semibold" color="text.default">
							{t('sidePanel.historyTitle')}
						</Text>
					</HStack>
				}
				right={
					<HStack spacing={0}>
						<Tooltip label={t('sidePanel.newChat')} hasArrow={false}>
							<IconButton
								aria-label={t('sidePanel.newChat')}
								icon={<PencilAltOutline boxSize={5} />}
								variant="ghost"
								size="sm"
								color="secondary.fg"
								_hover={{ bg: 'secondary.bgAlpha' }}
								onClick={onNewConversation}
							/>
						</Tooltip>
						<Tooltip label={t('sidePanel.close')} hasArrow={false}>
							<IconButton
								aria-label={t('sidePanel.close')}
								icon={<XOutline boxSize={5} />}
								variant="ghost"
								size="sm"
								color="secondary.fg"
								_hover={{ bg: 'secondary.bgAlpha' }}
								onClick={() => closePanel()}
							/>
						</Tooltip>
					</HStack>
				}
			/>

			<Box px={4} pt={2} pb={3}>
				<InputGroup size="sm">
					<InputLeftElement pointerEvents="none" h="100%" pl={2}>
						<SearchAltOutline boxSize={4} color="text.softest" />
					</InputLeftElement>
					<Input
						value={searchValue}
						onChange={(event) => setSearchValue(event.target.value)}
						placeholder={t('sidePanel.searchPlaceholder')}
						borderRadius="12px"
						bg="bg.canvas"
						borderColor="border.default"
						py={2}
						pr={3}
						h="auto"
					/>
				</InputGroup>
			</Box>

			<Box flex={1} overflowY="auto" overscrollBehavior="contain" px={3} pb={3}>
				<VStack align="stretch" spacing={3}>
					{isLoading && (
						<Text fontSize="sm" color="text.soft" px={2} py={2}>
							{t('thinking')}
						</Text>
					)}
					{!isLoading &&
						groupedConversations.map(({ group, conversations: groupConvos }) => (
							<Box key={group}>
								<Text px={2} pt={3} pb={1} fontSize="sm" fontWeight="medium" color="text.softest">
									{t(TIME_GROUP_KEYS[group])}
								</Text>
								{groupConvos.map((conversation) => {
									const title = getConversationTitle(conversation);
									const isRenaming = renamingId === conversation.conversationId;

									return (
										<Flex
											key={conversation.conversationId}
											align="center"
											w="100%"
											px={2}
											py={2}
											borderRadius="md"
											cursor="pointer"
											_hover={{ bg: 'secondary.bgAlpha' }}
											role="group"
										>
											<Box flex={1} minW={0} onClick={() => setSelectedConversationId(conversation.conversationId)}>
												{isRenaming ? (
													<Input
														value={renameValue}
														onChange={(e) => setRenameValue(e.target.value)}
														onBlur={handleFinishRename}
														onKeyDown={(e) => {
															if (e.key === 'Enter') handleFinishRename();
															if (e.key === 'Escape') {
																renameCommittedRef.current = true;
																setRenamingId(undefined);
																setRenameValue('');
															}
														}}
														size="sm"
														fontSize="md"
														fontWeight="medium"
														variant="outline"
														px={1}
														h={7}
														borderRadius="lg"
														borderColor="primary.fg"
														borderWidth="1.5px"
														_focus={{ borderColor: 'primary.fg', boxShadow: 'none' }}
														autoFocus
														onClick={(e) => e.stopPropagation()}
													/>
												) : (
													<Text fontSize="md" fontWeight="medium" color="text.default" noOfLines={1}>
														{title}
													</Text>
												)}
											</Box>
											<Box flexShrink={0} opacity={0} _groupHover={{ opacity: 1 }} transition="opacity 0.1s">
												<Menu>
													<Tooltip label={t('sidePanel.moreActions')} hasArrow={false}>
														<MenuButton
															as={IconButton}
															aria-label={t('sidePanel.moreActions')}
															icon={<DotsHorizontalOutline boxSize={4} />}
															variant="ghost"
															size="xs"
															color="text.softer"
															_hover={{ bg: 'secondary.bgAlphaHover' }}
															onClick={(e: React.MouseEvent) => e.stopPropagation()}
															borderRadius="lg"
														/>
													</Tooltip>
													<MenuList minW="120px" zIndex="popover">
														<MenuItem
															icon={<PencilOutline boxSize={5} />}
															onClick={(e) => {
																e.stopPropagation();
																handleRename(conversation.conversationId);
															}}
														>
															{t('sidePanel.renameChat')}
														</MenuItem>
														<MenuItem
															color="error.fg"
															icon={<TrashOutline boxSize={5} />}
															onClick={(e) => {
																e.stopPropagation();
																handleDeleteClick(conversation.conversationId);
															}}
														>
															{t('sidePanel.deleteChat')}
														</MenuItem>
													</MenuList>
												</Menu>
											</Box>
										</Flex>
									);
								})}
							</Box>
						))}
					{!isLoading && filteredConversations.length === 0 && (
						<Text fontSize="sm" color="text.soft" px={2}>
							{t('sidePanel.noHistory')}
						</Text>
					)}
				</VStack>
			</Box>

			<AlertDialog leastDestructiveRef={deleteCancelRef as RefObject<HTMLButtonElement>} isOpen={deleteDialog.isOpen} onClose={deleteDialog.onClose} isCentered>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							{t('sidePanel.deleteChatTitle')}
						</AlertDialogHeader>
						<AlertDialogCloseButton />
						<AlertDialogBody>
							<Text>{t('sidePanel.deleteChatConfirm')}</Text>
						</AlertDialogBody>
						<AlertDialogFooter gap={3}>
							<Button ref={deleteCancelRef} variant="outline" colorScheme="secondary" onClick={deleteDialog.onClose}>
								{t('sidePanel.cancel')}
							</Button>
							<Button variant="solid" colorScheme="danger" onClick={handleConfirmDelete}>
								{t('sidePanel.deleteChat')}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</Flex>
	);
}
